// Cliente da API. Escritas levam uma chave de idempotência: se a internet cair e o
// celular repetir o pedido, o servidor devolve o mesmo resultado em vez de duplicar.
export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) { super(message); }
}

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));
// o mesmo pedido (mesmo endereço e mesmos dados) enquanto não deu certo reusa a chave, por até 2 min:
// se o primeiro chegou ao servidor e só a resposta se perdeu, o segundo recebe a mesma resposta
const KEEP_MS = 120_000;
const keys = new Map<string, { key: string; at: number }>();
const inflight = new Map<string, Promise<unknown>>();

// identifica este aparelho: ao mudar algo, o servidor avisa os outros e este ignora o próprio aviso
export const CLIENT_ID = crypto.randomUUID();

async function send(method: string, url: string, body: unknown, key: string | null) {
  const headers: Record<string, string> = { 'X-Client-Id': CLIENT_ID };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (key) headers['Idempotency-Key'] = key;
  return fetch('/api' + url, { method, credentials: 'same-origin', headers, body: body !== undefined ? JSON.stringify(body) : undefined });
}

async function call<T>(method: string, url: string, body?: unknown): Promise<T> {
  if (method === 'GET') return finish<T>(await send(method, url, undefined, null));
  const print = `${method} ${url} ${JSON.stringify(body ?? {})}`;
  // toque duplo: o segundo toque espera o primeiro e recebe o mesmo resultado
  const running = inflight.get(print);
  if (running) return running as Promise<T>;
  const p = (async () => {
    const kept = keys.get(print);
    const key = kept && Date.now() - kept.at < KEEP_MS ? kept.key : crypto.randomUUID();
    keys.set(print, { key, at: Date.now() });
    // sem internet ou servidor ocupado: tenta de novo sozinho, com a mesma chave
    for (let i = 0; ; i++) {
      let res: Response;
      try { res = await send(method, url, body ?? {}, key); } catch {
        if (i < 2) { await wait(700 * (i + 1)); continue; }
        throw new ApiError(0, 'OFFLINE', 'Sem conexão. Nada foi gravado em dobro: tente de novo quando a internet voltar.');
      }
      if ((res.status === 409 || res.status >= 502) && i < 3) {
        const peek = res.status === 409 ? await res.clone().json().catch(() => null) : null;
        if (res.status >= 502 || peek?.error?.code === 'IN_PROGRESS') { await wait(800 * (i + 1)); continue; }
      }
      // deu certo, ou o servidor recusou com motivo: a próxima tentativa já é um pedido novo
      if (res.status < 500) keys.delete(print);
      return finish<T>(res);
    }
  })();
  inflight.set(print, p);
  try { return await p; } finally { inflight.delete(print); }
}

async function finish<T>(res: Response): Promise<T> {
  const data = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) {
    const e = data?.error ?? {};
    throw new ApiError(res.status, e.code ?? 'ERROR', e.message ?? 'Não foi possível concluir. Tente de novo.');
  }
  return data as T;
}

export const api = {
  get: <T>(url: string) => call<T>('GET', url),
  post: <T>(url: string, body: unknown = {}) => call<T>('POST', url, body),
  put: <T>(url: string, body: unknown) => call<T>('PUT', url, body),
};

export const newKey = () => crypto.randomUUID();

export interface Me {
  user: { id: string; name: string; email: string };
  role: string;
  permissions: string[];
  professionalId: string | null;
  salon: { id: string; name: string; slug: string };
}

export interface AgendaItem {
  id: string;
  date: string;
  time: string;
  status: string;
  client: string;
  phone: string;
  service: string;
  priceCents: number;
  professionalId: string;
  depositStatus: string;
}

export const money = (c: number) => (c / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
